import { dispatchCartUpdated } from "./pent-core/events.js"

/**
 * PlQuantityStepper class for the quantity stepper
 * @extends {HTMLElement}
 */
class PlQuantityStepper extends HTMLElement {
  #abortController = null

  constructor() {
    super()

    // Create refs object to match the original API
    this.refs = {
      quantityInput: this.querySelector('[ref="quantityInput"]'),
      quantityButtons: this.querySelectorAll('[ref="quantityButtons[]"]'),
    }

    // Add event listeners
    this.addEventListener("click", this.handleClick.bind(this))
    this.refs.quantityInput?.addEventListener("input", this.setQuantity.bind(this))
    this.refs.quantityInput?.addEventListener("focus", this.selectInputValue.bind(this))
  }

  connectedCallback() {
    this.#updateButtonStates()

    requestAnimationFrame(() => {
      requestAnimationFrame(() => this.#updateButtonStates())
    })
  }

  disconnectedCallback() {
    this.#abortController?.abort()
  }

  /** @returns {string|null} Cart line key if in cart context */
  get cartLineKey() {
    return this.dataset.cartLineKey || null
  }

  handleClick(event) {
    if (event.target.closest('[name="plus"]')) {
      event.preventDefault()
      this.#tryIncrement(event)
    } else if (event.target.closest('[name="minus"]')) {
      event.preventDefault()
      this.refs.quantityInput.stepDown()
      this.#onQuantityChange(event)
    }
  }

  /**
   * Handles the quantity increase event.
   * @param {Event} event - The event.
   */
  increaseQuantity(event) {
    if (!(event.target instanceof HTMLElement)) return

    event.preventDefault()
    this.#tryIncrement(event)
  }

  /**
   * Handles the quantity decrease event.
   * @param {Event} event - The event.
   */
  decreaseQuantity(event) {
    if (!(event.target instanceof HTMLElement)) return

    event.preventDefault()
    this.refs.quantityInput.stepDown()
    this.#onQuantityChange(event)
  }

  /**
   * When our input gets focused, we want to fully select the value.
   * @param {FocusEvent} event
   */
  selectInputValue(event) {
    const { quantityInput } = this.refs
    if (!(event.target instanceof HTMLInputElement) || document.activeElement !== quantityInput)
      return

    quantityInput.select()
  }

  /**
   * Handles the quantity set event.
   * @param {Event} event - The event.
   */
  setQuantity(event) {
    if (!(event.target instanceof HTMLElement)) return

    event.preventDefault()
    if (event.target instanceof HTMLInputElement) {
      this.refs.quantityInput.value = event.target.value
    }
    this.#onQuantityChange(event)
  }

  /**
   * Handles the quantity change event.
   * @param {Event} event - The event.
   */
  #onQuantityChange(event) {
    this.#checkQuantityRules()
    this.#updateButtonStates()

    // If in cart context, update via API
    if (this.cartLineKey) {
      this.#updateCart(this.cartLineKey, parseInt(this.refs.quantityInput.value))
    }
  }

  #tryIncrement(event) {
    const max = parseInt(this.refs.quantityInput.max) || Infinity
    const currentValue = parseInt(this.refs.quantityInput.value) || 0

    if (currentValue >= max) {
      this.#showError(`Product quantity limit reached.`)
      return
    }

    this.refs.quantityInput.stepUp()
    this.#onQuantityChange(event)
  }

  /**
   * Updates cart via Shopify API and dispatches cart:updated
   * @param {string} lineKey - Cart line item key
   * @param {number} quantity - New quantity
   */
  async #updateCart(lineKey, quantity) {
    this.#abortController?.abort()
    this.#abortController = new AbortController()
    this.classList.add("pl-quantity-stepper--loading")

    try {
      const url = (window.Shopify?.routes?.root || "/") + "cart/change.js"

      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          id: lineKey,
          quantity: quantity,
        }),
        signal: this.#abortController.signal,
      })

      const data = await response.json()

      if (!response.ok) {
        const error = await response.json()

        // Prevent browser from showing native validation error
        // (Note: event is not defined here, relying on catch block logic)

        // Dispatch custom error event
        // We don't have variantId here easily unless we store it, but cart error usually sufficient for line items
        document.dispatchEvent(
          new CustomEvent("cart:error", {
            detail: {
              message: error.description || error.message || "Failed to update cart",
              lineKey: lineKey,
            },
            bubbles: true,
          })
        )

        // Revert value if needed or just let the re-render handle it
        // Usually checking against max is handled by checkQuantityRules if max is updated
        const lineItem = data.items?.find((item) => item.key === lineKey)
        if (lineItem) {
          this.refs.quantityInput.value = lineItem.quantity
          this.#checkQuantityRules()
          this.#updateButtonStates()
        }

        throw new Error(error.description || "Failed to update cart")
      }

      dispatchCartUpdated(document, { cart: data })
    } catch (error) {
      if (error.name !== "AbortError") console.error("Failed to update cart:", error)
    } finally {
      this.classList.remove("pl-quantity-stepper--loading")
    }
  }

  /**
   * Shows an error message to the user
   * @param {string} message - Error message
   */
  #showError(message) {
    // Option 1: Dispatch event for a toast/notification system
    document.dispatchEvent(
      new CustomEvent("cart:error", {
        detail: { message, lineKey: this.cartLineKey },
        bubbles: true,
      })
    )

    // Visual feedback on the stepper
    this.classList.add("pl-quantity-stepper--error")
    setTimeout(() => this.classList.remove("pl-quantity-stepper--error"), 600)
  }

  /**
   * Checks the quantity rules are met
   */
  #checkQuantityRules = () => {
    const { quantityInput } = this.refs
    const min = parseInt(quantityInput.min) || 0
    const max = parseInt(quantityInput.max) || Infinity
    const currentValue = parseInt(quantityInput.value) || 0

    if (isNaN(currentValue) || currentValue < min) {
      quantityInput.value = min
    } else if (currentValue > max) {
      // Revert to max
      quantityInput.value = max

      // Dispatch error if we are hitting the ceiling
      // We need to know if this was a user interaction or just initialization.
      // This method is called from onQuantityChange, which is user interaction.
      this.#showError(`Product quantity limit reached.`)
    }
  }

  /**
   * Updates the disabled state of buttons based on current value
   */
  #updateButtonStates = () => {
    const { quantityInput, quantityButtons } = this.refs
    const min = parseInt(quantityInput.min) || 0
    // const max = parseInt(quantityInput.max) || Infinity // We still need max for other logic, but not for disabling plus
    const currentValue = parseInt(quantityInput.value) || 0
    const isDisabled = this.hasAttribute("disabled")
    if (isDisabled) return

    const minusButton = Array.from(quantityButtons).find((btn) => btn.name === "minus")
    const plusButton = Array.from(quantityButtons).find((btn) => btn.name === "plus")

    if (minusButton) {
      minusButton.disabled = currentValue <= min
    }

    // Don't disable plus button anymore
    // if (plusButton) {
    //   plusButton.disabled = currentValue >= max
    // }
  }

  /**
   * Gets the quantity input.
   * @returns {HTMLInputElement} The quantity input.
   */
  get quantityInput() {
    if (!this.refs.quantityInput) {
      throw new Error('Missing <input ref="quantityInput" /> inside <pl-quantity-stepper />')
    }

    return this.refs.quantityInput
  }
}

if (!customElements.get("pl-quantity-stepper")) {
  customElements.define("pl-quantity-stepper", PlQuantityStepper)
}
